const RELOADED_AT_KEY = 'new-build-reloaded-at';
const COOLDOWN_MS = 60_000;

/**
 * Reloads the page to pick up the current build, at most once per cooldown window.
 *
 * The cooldown is time-boxed rather than once-per-session: a tab left open across two deploys
 * needs to reload twice. Storage is best-effort — `sessionStorage` can throw in private or
 * partitioned contexts, and recovering the build beats a theoretical reload loop. Returns whether
 * the reload was issued.
 */
export function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
    if (Date.now() - last < COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — still recover the build.
  }
  window.location.reload();
  return true;
}

let loadedBuildId: string | undefined;
let reloadPending = false;

/**
 * Tracks the build id stamped on `/v3/config`. The first id seen is the build this tab loaded;
 * a later refetch returning a different one means a deploy happened underneath the tab.
 *
 * This only marks the tab — it does not reload. The refetch that notices is usually a network
 * reconnect, and the page is still working at that moment: reloading there would discard an
 * assessment in progress, a SCORM player's position or an unsent draft, none of which have a
 * `beforeunload` guard. `applyPendingReloadOnNavigation` takes it at the next route change,
 * where the user is leaving that state anyway.
 */
export function checkBuildId(buildId: string | undefined): void {
  if (!buildId) return;
  if (loadedBuildId === undefined) {
    loadedBuildId = buildId;
    return;
  }
  if (buildId !== loadedBuildId) reloadPending = true;
}

export function isReloadPending(): boolean {
  return reloadPending;
}

// Just enough of the data router to watch committed navigations.
interface NavigationSource {
  state: { location: { pathname: string } };
  subscribe: (fn: (state: { location: { pathname: string } }) => void) => unknown;
}

/**
 * Reloads at the first route change after a new build was seen, so the tab stops running old
 * code against the new server without interrupting whatever it was in the middle of.
 *
 * Keyed on the committed pathname: history has moved to the destination by then, and it is the
 * same notion of "leaving" that `FormNavigationBlocker`'s `useBlocker` uses. A changing
 * `location.key` would also fire on search-param and `replace` navigations — tab switches,
 * filters, the site builder's section selection — which are not the user leaving anything.
 */
export function applyPendingReloadOnNavigation(router: NavigationSource): void {
  let lastPath = router.state.location.pathname;
  router.subscribe((state) => {
    if (state.location.pathname === lastPath) return;
    lastPath = state.location.pathname;
    if (reloadPending) reloadForNewBuild();
  });
}
